import { ClaimReservationWithTimeslot, ScheduleException } from '@Generated/back-office-client/src'
import { GridDisplayMode, ReservationGridDisplayItem } from './types'
import { format, parse } from 'date-fns'
import { difference } from 'lodash'
import { formatExceptionHoursToMatchClaimCenter, formatToTimeDisplay } from '@Components/util/utils'

const DATE_FORMAT_STRING = 'yyyy-MM-dd HH:mm:ss'

export const mapReservationToDisplay = (
  claim: ClaimReservationWithTimeslot,
  gridDisplayMode: GridDisplayMode,
  currentDay: string,
): ReservationGridDisplayItem => {
  const startTime = parse(
    `${currentDay} ${claim.timeslot.display.isoStartTime}`,
    DATE_FORMAT_STRING,
    new Date(),
  )
  const endTime = parse(
    `${currentDay} ${claim.timeslot.display.isoEndTime}`,
    DATE_FORMAT_STRING,
    new Date(),
  )

  return {
    channelUuid:
      gridDisplayMode === GridDisplayMode.DAY
        ? claim.timeslot.bookableId!
        : claim.timeslot.display.localDate,
    id: claim.id,
    title: `${claim.claimReservationDetails.name}`,
    description: `(${claim.claimReservationDetails.numberOfTickets.toString()} tickets)`,
    subDescription: claim.claimReservationDetails.phone ?? '',
    timeslot: claim.timeslot,
    formattedStartTime: format(startTime, 'h:mmaaa').toLowerCase(),
    formattedEndTime: format(endTime, 'h:mmaaa').toLowerCase(),
    since: startTime.toISOString(),
    till: endTime.toISOString(),
    image: '',
    isReservation: true,
    index: 0,
    channelIndex: 0,
    channelPosition: {
      top: 0,
      height: 0,
    },
    gridDisplayMode,
    claimFormId: claim.claimFormId ?? null,
    claimFormAuthorized: claim.claimFormAuthorized ?? false,
    checkedInAt: claim.claimReservationDetails.checkedInAt ?? null,
    noShowAt: claim.claimReservationDetails.noShowAt ?? null,
  }
}

export const mapExceptionToDisplay = (
  exception: ScheduleException,
  gridDisplayMode: GridDisplayMode,
  channelId: string,
  bookableId: string,
  currentDay: string,
  claimCenterStartHour: number,
  claimCenterEndHour: number,
): ReservationGridDisplayItem => {
  const startTime = parse(
    `${currentDay} ${exception.localStartTime}`,
    DATE_FORMAT_STRING,
    new Date(),
  )
  const endTime = parse(`${currentDay} ${exception.localEndTime}`, DATE_FORMAT_STRING, new Date())

  const { startTime: correctedStartTime, endTime: correctedEndTime } =
    formatExceptionHoursToMatchClaimCenter(
      startTime,
      endTime,
      claimCenterStartHour,
      claimCenterEndHour,
    )

  return {
    channelUuid: channelId,
    id: `${exception.id}:${bookableId}`,
    title: 'Unavailable to book',
    description: exception.reason,
    since: startTime.toISOString(),
    till: endTime.toISOString(),
    formattedStartTime: formatToTimeDisplay(correctedStartTime),
    formattedEndTime: formatToTimeDisplay(correctedEndTime),
    image: '',
    isReservation: false,
    index: 0,
    channelIndex: 0,
    channelPosition: {
      top: 0,
      height: 0,
    },
    gridDisplayMode,
  }
}

// Fill dates that have one or fewer reservations or exceptions to have a width of two items
export const getFillerData = (
  displayedDates: string[],
  gridItems: ReservationGridDisplayItem[],
  gridDisplayMode: GridDisplayMode,
  currentDay: string,
): ReservationGridDisplayItem[] => {
  if (gridDisplayMode === GridDisplayMode.DAY) {
    return []
  }

  // Count how many items are on each day
  const datesWithCounts = gridItems.reduce(
    (acc, item) => {
      const bookableId = item.timeslot ? item.timeslot.bookableId : item.id.split(':')[1]
      const key = item.channelUuid
      const subKey = `${bookableId}:${item.isReservation ? 'res' : 'exception'}`
      if (acc[key]) {
        if (acc[key][subKey]) {
          acc[key][subKey] = acc[key][subKey] + 1
        } else {
          acc[key][subKey] = 1
        }
      } else {
        acc[key] = {
          [`${bookableId}:res`]: item.isReservation ? 1 : 0,
          [`${bookableId}:exception`]: item.isReservation ? 0 : 1,
        }
      }

      return acc
    },
    {} as Record<string, Record<string, number>>,
  )

  // Filter out the items that are one or greater
  const datesWithData = Array.from(
    new Set(
      Object.entries(datesWithCounts)
        .filter(([_, value]) => {
          const resBookables = Object.entries(value).filter(
            ([key, value]) => key.split(':')[1] === 'res' && value > 0,
          )
          const exceptionBookables = Object.entries(value).filter(
            ([key, value]) => key.split(':')[1] === 'exception' && value > 0,
          )

          if (resBookables.length > 1 || exceptionBookables.length > 1) {
            return true
          }

          return false
        })
        .map((e) => e[0]),
    ),
  )

  const datesWithoutData = difference(displayedDates, datesWithData)

  const startTime = parse(`${currentDay} 12:00:00`, DATE_FORMAT_STRING, new Date())
  const endTime = parse(`${currentDay} 12:01:00`, DATE_FORMAT_STRING, new Date())

  return datesWithoutData.flatMap((date) => {
    return new Array(2).fill(null).map((_, index) => ({
      channelUuid: date,
      id: `filler:${date}:${index}`,
      title: 'Filler',
      description: 'For filling',
      since: startTime,
      till: endTime,
      formattedStartTime: '',
      formattedEndTime: '',
      image: '',
      isReservation: false,
      index: 0,
      channelIndex: 0,
      channelPosition: {
        top: 0,
        height: 0,
      },
      gridDisplayMode,
    }))
  })
}
